import { Core } from "@faire/design-tokens";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { Pin } from "@faire/web/slate/icons/foundation/Pin";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

const DROPDOWN_MAX_HEIGHT = 200;

export const CANT_FIND_OPTION_ID = "cantfind";

interface IProps {
  data?: IMatchedBusiness[];
  onSelect: (matchedBusiness?: IMatchedBusiness, storeName?: string) => void;
  isMobileOverlayOpen: boolean;
  query: string;
  hasStoreName: boolean;
  isInsideModal?: boolean;
  tipDescriptionText?: string;
}

export const StoreSearchDropdown: React.FC<IProps> = ({
  data = [],
  isMobileOverlayOpen,
  query,
  onSelect,
  hasStoreName,
  isInsideModal,
  tipDescriptionText,
}) => {
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const showTip = !data.length && !hasStoreName;
  const manualMatchBusiness = IMatchedBusiness.build({
    id: CANT_FIND_OPTION_ID,
    name: query,
    type: IMatchedBusiness.Type.MANUAL,
  });

  useEffect(() => {
    if (isMobileOverlayOpen) {
      getWindow()?.scrollTo?.(0, 0);
    }
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex === data.length) {
          onSelect(manualMatchBusiness, manualMatchBusiness.name);
          e.stopPropagation();
          return;
        }
        onSelect(data[highlightedIndex], data[highlightedIndex]?.name);
        e.stopPropagation();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex(
          highlightedIndex < data.length ? highlightedIndex + 1 : 0
        );
        e.stopPropagation();
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(
          highlightedIndex > 0 ? highlightedIndex - 1 : data.length
        );
        e.stopPropagation();
      }
    };
    getWindow()?.addEventListener("keydown", handleKeyDown);
    return () => getWindow()?.removeEventListener("keydown", handleKeyDown);
  }, [data, highlightedIndex, manualMatchBusiness, onSelect]);

  return (
    <DropdownContainer $isInsideModal={!!isInsideModal}>
      <DropdownWrapper
        data-test-id="stores-search-dropdown"
        $isMobileOverlayOpen={isMobileOverlayOpen}
        $showTip={showTip}
        $isInsideModal={!!isInsideModal}
      >
        {showTip ? (
          <Tip tipDescriptionText={tipDescriptionText} />
        ) : (
          <>
            {data.map((matchBusiness, index) => (
              <Option
                key={matchBusiness.id}
                matchBusiness={matchBusiness}
                onSelect={() => onSelect(matchBusiness, matchBusiness.name)}
                onMouseOver={() => setHighlightedIndex(index)}
                focused={index === highlightedIndex}
              />
            ))}
            <Option
              key={CANT_FIND_OPTION_ID}
              matchBusiness={manualMatchBusiness}
              onSelect={() =>
                onSelect(manualMatchBusiness, manualMatchBusiness.name)
              }
              onMouseOver={() => setHighlightedIndex(data.length)}
              focused={data.length === highlightedIndex}
            />
          </>
        )}
      </DropdownWrapper>
    </DropdownContainer>
  );
};

interface IOption {
  matchBusiness: IMatchedBusiness;
  onSelect: () => void;
  onMouseOver: () => void;
  focused?: boolean;
}

const Option: React.FC<IOption> = ({
  matchBusiness,
  onSelect,
  onMouseOver,
  focused,
}) => {
  return (
    <OptionContainer
      onClick={onSelect}
      onMouseOver={onMouseOver}
      $isFocused={focused}
      data-test-id={`stores-search-dropdown-option-${matchBusiness.id}`}
    >
      <IconWrapper $isFocused={focused}>
        <Pin width="18px" />
      </IconWrapper>
      <Spacer width={12} />
      <Column justify="center">
        {matchBusiness.id === CANT_FIND_OPTION_ID ? (
          <TextUnderline>
            <StrictLocalMsg
              defaultMessage="Enter store name manually"
              description={{
                text: "label for option to manually enter store name",
                img: "https://cdn.faire.com/fastly/3292c2b4e930bd45b8b5e72042cda2d9b18cb7561ec5d235c5c4de8e46662bc5.png",
              }}
            />
          </TextUnderline>
        ) : (
          <>
            <Typography maxLines={1}>{matchBusiness.name}</Typography>
            <Typography
              variant="labelSansRegular"
              color={Core.text.subdued}
              maxLines={1}
            >
              {matchBusiness.location}
            </Typography>
          </>
        )}
      </Column>
    </OptionContainer>
  );
};

const Tip: React.FC<{ tipDescriptionText?: string }> = ({
  tipDescriptionText,
}) => {
  return (
    <TipWrapper>
      <Typography
        variant="paragraphSansMedium"
        color={Core.text.subdued}
        align={{ mobileAndAbove: "center", tabletAndAbove: "start" }}
      >
        <StrictLocalMsg
          defaultMessage="Search by store name or address"
          description={{
            text: "Tip for entering store name or address",
            img: "https://cdn.faire.com/fastly/be8d57c24ea7daf3037cdca4092b0a719e37a4e26f702237aef74058d35a6dff.png",
          }}
        />
      </Typography>
      <Typography
        align={{ mobileAndAbove: "center", tabletAndAbove: "start" }}
        variant="paragraphSansRegular"
        color={Core.text.subdued}
      >
        {tipDescriptionText ? (
          tipDescriptionText
        ) : (
          <StrictLocalMsg
            defaultMessage="If you have several stores, use your primary retail location. You can add additional locations later."
            description={{
              text: "Asking the retailer to enter one store location even if they have multiple stores",
              img: "https://cdn.faire.com/fastly/be8d57c24ea7daf3037cdca4092b0a719e37a4e26f702237aef74058d35a6dff.png",
            }}
          />
        )}
      </Typography>
    </TipWrapper>
  );
};

const TextUnderline = styled(Typography)`
  text-decoration: underline;
`;

const IconWrapper = styled.div<{ $isFocused?: boolean }>`
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 32px;
  ${(props) =>
    props.$isFocused
      ? css`
          background-color: ${Core.surface.inverse};
        `
      : css`
          background-color: ${Core.surface.subdued};
        `}
`;

const OptionContainer = styled(Row)<{ $isFocused?: boolean }>`
  width: 100%;
  cursor: pointer;
  padding: ${getSpacing(12, "2x")};
  ${(props) =>
    props.$isFocused &&
    css`
      background-color: ${Core.surface.subdued};
    `}
`;

const DropdownContainer = styled.div<{ $isInsideModal: boolean }>`
  ${({ $isInsideModal }) =>
    $isInsideModal
      ? css`
          position: relative;
        `
      : css`
          ${responsiveMediaQueries.tabletAndAbove} {
            position: relative;
          }
        `}
`;

const DropdownWrapper = styled(Column)<{
  $isMobileOverlayOpen: boolean;
  $showTip: boolean;
  $isInsideModal: boolean;
}>`
  position: absolute;
  width: 100%;
  background-color: ${Core.surface.inverse};
  z-index: 1;
  ${({ $isMobileOverlayOpen }) =>
    $isMobileOverlayOpen
      ? css`
          box-shadow: none;
        `
      : css`
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
        `}
  ${({ $showTip }) =>
    $showTip &&
    css`
      top: 40%;
    `}

  ${({ $isInsideModal }) =>
    $isInsideModal
      ? css`
          overflow-y: auto;
          z-index: 2;
          height: auto;
          max-height: ${DROPDOWN_MAX_HEIGHT}px;
          border: solid 1px ${Core.border.muted};
        `
      : css``}
`;

const TipWrapper = styled(Column)`
  padding: ${getSpacing("3x", "5x")};
`;
